import startCase from 'lodash/startCase';
import {
  type CategoryOption,
  type CategoryOptions,
  type NonEmptyAvailabilityOptions,
} from '@discover-data/stores/restaurant/gql/getters/allAvailabilitySearch';
import usePeople from '@discover-data/stores/hooks/people/usePeople';
import Button from '@discover-ui/button';
import { RadioGroup, RadioGroupItem } from '@discover-ui/radioGroup';
import Label from '@discover-ui/label';
import { cn } from '@discover-lib/utils';
import { currencyFormat } from '@discover-lib/dataFormats';
import { timeOfLeaveTime } from '@discover-lib/dateFormats';
import { type WithRegularBooking } from '@/components/hocs/withRegularBooking';
import { useAvailabilityOptionsDialogContext } from './dialog/context';

type Option = {
  id: string;
  name: string;
  description?: string;
};

function normaliseDescription(option: CategoryOption, currency: string): string {
  const description = 'description' in option ? option.description : undefined;
  const price = 'price' in option ? option.price : undefined;
  const leaveTime = 'leaveTime' in option ? option.leaveTime : undefined;
  const formattedLeaveTime = leaveTime ? timeOfLeaveTime(leaveTime) : false;
  const paymentRequired = 'paymentRequired' in option ? option.paymentRequired : undefined;

  return `
    ${description ?? ''}
    ${price ? `Price: ${currencyFormat(price, currency)} per person.` : ''}
    ${formattedLeaveTime ? `Leave time: ${formattedLeaveTime}.` : ''}
    ${paymentRequired ? `Please book this option direct with the venue.` : ''}
  `;
}

function isValidOption(option: CategoryOption): option is Option {
  return !!option.id && !!option.name;
}

function normaliseOptions(options: CategoryOptions): CategoryOption[] {
  if (!options) {
    return [];
  }

  const optionsArray = Array.isArray(options) ? options : Object.values(options);

  return optionsArray.filter(isValidOption);
}

export type SelectedOptionIdByCategory = { [category: string]: string | undefined };

export type AvailabilityOptionsFormProps = {
  options: NonEmptyAvailabilityOptions;
  selectedOptionIds: SelectedOptionIdByCategory;
  setSelectedOptionIds: (newSelectedOptions: SelectedOptionIdByCategory) => void;
  currency: string;
  className?: string;
} & Pick<WithRegularBooking, 'handleBooking' | 'isProcessing'>;

function AvailabilityOptionsForm({
  options,
  selectedOptionIds,
  setSelectedOptionIds,
  currency,
  handleBooking,
  isProcessing,
  className,
}: AvailabilityOptionsFormProps) {
  const { selectedSlot } = useAvailabilityOptionsDialogContext();
  const [people] = usePeople();

  return (
    <form
      className={cn('flex flex-col gap-4', className)}
      onSubmit={(event) => {
        event.preventDefault();

        const selectedOptions = Object.fromEntries(
          Object.entries(options).map(([categoryName, categoryOptions]) => {
            const normalisedOptions = normaliseOptions(categoryOptions);
            const selectedOption = normalisedOptions.find(
              (option) => `${option.id}` === `${selectedOptionIds[categoryName]}`
            );
            const defaultOption = normalisedOptions[0];

            return [categoryName, selectedOption || defaultOption];
          })
        );

        handleBooking({
          time: selectedSlot.time,
          guests: people,
          options: selectedOptions,
          timeSlot: selectedSlot,
        });
      }}>
      {Object.entries(options).map(([categoryName, categoryOptions]) => {
        const normalisedOptions = normaliseOptions(categoryOptions);

        if (normalisedOptions.length === 0) {
          return;
        }

        return (
          <div key={categoryName} className="flex flex-col gap-2">
            <h4 className="font-semibold">{startCase(categoryName)}</h4>
            <RadioGroup
              orientation="vertical"
              className="gap-2"
              defaultValue={`${normalisedOptions[0].id}`}
              value={selectedOptionIds[categoryName]}
              onValueChange={(id) =>
                setSelectedOptionIds({
                  ...selectedOptionIds,
                  [categoryName]: id,
                })
              }
              name={categoryName}>
              {normalisedOptions.map((option) => {
                const { id, name } = option;

                const normalisedDescription = normaliseDescription(option, currency);

                return (
                  <div key={id} className="flex items-start gap-2">
                    <RadioGroupItem value={`${id}`} id={`${id}`} />
                    <Label className="flex flex-1 flex-col" htmlFor={`${id}`}>
                      <span className="text-sm leading-tight">{name}</span>
                      {!!normalisedDescription && (
                        <span className="text-[11px]">{normalisedDescription}</span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        );
      })}
      <Button
        kind={isProcessing ? 'unavailable' : 'normal'}
        disabled={isProcessing}
        loading={isProcessing}
        className="ml-auto mt-2 w-auto"
        type="submit">
        Next
      </Button>
    </form>
  );
}

export default AvailabilityOptionsForm;
